import {
  Box,
  Button,
  HStack,
  IconButton,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Portal,
  Skeleton,
  Text,
  VStack,
  useDisclosure,
} from "@chakra-ui/react";
import { AlertTriangle, Bell } from "../../../shared/icons";
import { EmptyState } from "../../../shared/ui";
import AlertsList from "./AlertsList";
import {
  useMarkAlertReadMutation,
  useMarkAlertUnreadMutation,
  useMarkAllAlertsReadMutation,
} from "../hooks/useAlertReadMutations";

// Sino da Navbar: o contador mostra só os alertas não lidos e o clique abre a mesma
// lista de alertas da Visão Geral, com um checkbox de "lido" por alerta e a ação de
// marcar todas. Os lidos descem para a seção "Lidas", esmaecidos. Fecha ao seguir a
// ação de um alerta, já que a navegação leva o usuário para outra tela.
export default function NotificationsPopover({ alertas = [], isLoading, isError }) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const markRead = useMarkAlertReadMutation();
  const markUnread = useMarkAlertUnreadMutation();
  const markAllRead = useMarkAllAlertsReadMutation();

  const naoLidos = alertas.filter((a) => !a.read);
  const lidos = alertas.filter((a) => a.read);
  const total = naoLidos.length;

  const alternarLido = (alerta) => (alerta.read ? markUnread : markRead).mutate(alerta.id);

  return (
    <Popover isOpen={isOpen} onOpen={onOpen} onClose={onClose} placement="bottom-end" isLazy>
      <Box position="relative">
        {/* O trigger envolve só o botão para que aria-expanded/aria-haspopup fiquem nele
            e o foco volte ao sino quando o popover fecha. */}
        <PopoverTrigger>
          <IconButton
            aria-label={total ? `Notificações (${total} não ${total > 1 ? "lidas" : "lida"})` : "Notificações"}
            icon={<Bell size={18} />}
            variant="ghost"
          />
        </PopoverTrigger>
        {total > 0 ? (
          <Box
            aria-hidden="true"
            position="absolute"
            top="2px"
            right="2px"
            boxSize="16px"
            borderRadius="full"
            bg="danger.500"
            color="white"
            fontSize="10px"
            display="flex"
            alignItems="center"
            justifyContent="center"
            pointerEvents="none"
          >
            {total > 9 ? "9+" : total}
          </Box>
        ) : null}
      </Box>
      <Portal>
        <PopoverContent w={{ base: "calc(100vw - 32px)", sm: "380px" }}>
          <PopoverHeader>
            <HStack justify="space-between" spacing={2}>
              <Text fontWeight="semibold">Notificações</Text>
              {!isLoading && !isError && alertas.length > 0 ? (
                <Button
                  size="xs"
                  variant="ghost"
                  onClick={() => markAllRead.mutate()}
                  isDisabled={total === 0}
                  isLoading={markAllRead.isPending}
                >
                  Marcar todas como lidas
                </Button>
              ) : null}
            </HStack>
          </PopoverHeader>
          <PopoverBody maxH="70vh" overflowY="auto">
            <NotificationsBody
              alertas={alertas}
              naoLidos={naoLidos}
              lidos={lidos}
              isLoading={isLoading}
              isError={isError}
              onNavigate={onClose}
              onToggleRead={alternarLido}
            />
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
}

function NotificationsBody({ alertas, naoLidos, lidos, isLoading, isError, onNavigate, onToggleRead }) {
  if (isLoading) {
    return (
      <VStack align="stretch" spacing={2} data-testid="notifications-loading">
        <Skeleton height="64px" borderRadius="md" />
        <Skeleton height="64px" borderRadius="md" />
      </VStack>
    );
  }

  if (isError) {
    return (
      <EmptyState
        icon={AlertTriangle}
        title="Não foi possível carregar os alertas."
        description="Tente novamente em alguns instantes."
      />
    );
  }

  if (alertas.length === 0) {
    return <AlertsList alertas={[]} />;
  }

  return (
    <VStack align="stretch" spacing={4}>
      {naoLidos.length > 0 ? (
        <AlertsList alertas={naoLidos} onNavigate={onNavigate} onToggleRead={onToggleRead} />
      ) : (
        <Text fontSize="sm" color="app-muted-fg">
          Nenhuma notificação nova.
        </Text>
      )}
      {lidos.length > 0 ? (
        <Box as="section" aria-label="Lidas">
          <Text fontSize="xs" fontWeight="semibold" color="app-muted-fg" textTransform="uppercase" mb={2}>
            Lidas
          </Text>
          <AlertsList alertas={lidos} onNavigate={onNavigate} onToggleRead={onToggleRead} />
        </Box>
      ) : null}
    </VStack>
  );
}
